/**
 * Lightweight API request function for React Router loaders.
 *
 * Loaders run outside the React component tree, so they cannot use
 * `AdminApiClient` from `AppProvider`.  This module provides a standalone
 * `loaderFetch()` that uses `getAuthToken()` (Clerk or admin-token) and
 * reads the API base URL from localStorage (same as `App.tsx`).
 *
 * The function signature matches `AdminApiClient.request<T>()` so loader
 * code feels familiar, but it's deliberately simpler — no `setBaseUrl()`,
 * no AbortController (loaders manage their own lifecycle via the router).
 *
 * @module loader-api
 */

import { defaultApiBase, isDashboardApiBase, readJsonResponse } from './api';
import { getAuthToken } from './auth-token';

/**
 * Resolve the API base URL exactly the same way as `App.tsx`:
 * 1. Read `apx.apiBase` from localStorage.
 * 2. If the stored value is a Pages origin (circular), ignore it.
 * 3. Fall back to `defaultApiBase()`.
 */
function resolveApiBase(): string {
  try {
    const stored = localStorage.getItem('apx.apiBase');
    if (stored && !isDashboardApiBase(stored)) {
      return stored.replace(/\/+$/, '');
    }
  } catch {
    // localStorage may be unavailable (SSR, privacy mode).
  }
  return defaultApiBase().replace(/\/+$/, '');
}

/**
 * Make an authenticated API request from a React Router loader.
 *
 * If no auth token is available (Clerk not signed in, or admin-token empty),
 * returns `null` instead of throwing.  The component can then fall back to
 * `useFetch` which will retry when auth becomes available.
 *
 * Network errors (401, 500, timeout) are still thrown and handled by the
 * route `errorElement`.
 *
 * @param path  API path (e.g. `/admin/providers`)
 * @param init  Optional `RequestInit` (method, body, etc.)
 * @returns Parsed JSON response body, or `null` when unauthenticated
 */
export async function loaderFetch<T>(path: string, init: RequestInit = {}): Promise<T | null> {
  const token = await getAuthToken();
  if (!token) return null;

  const headers = new Headers(init.headers);
  headers.set('authorization', `Bearer ${token}`);
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json');
  }

  const base = resolveApiBase();
  const url = `${base}${path.startsWith('/') ? path : `/${path}`}`;

  const response = await fetch(url, { ...init, headers });
  return readJsonResponse<T>(response);
}
